// Carztohire — content and static data
// Category structure and example vehicle photos aligned with SIXT US fleet reference.

export const BRAND = {
  name: "Carztohire",
  phone: "602 610 1396",
  phoneHref: "tel:+16026101396",
  email: "Info@carztohire.com",
  emailHref: "mailto:Info@carztohire.com",
  address: "1736 Holly Oak Ln, Chattanooga, TN 37421, USA",
  tagline: "Compare car rentals across every major US brand.",
};

export const PROVIDERS = [
  "Alamo",
  "Hertz",
  "Sixt",
  "Enterprise",
  "Budget",
  "Avis",
  "National",
  "Dollar",
  "Thrifty",
];

// SIXT side-view PNGs (publicly hosted, 1050x600, transparent background)
const SIXT = "https://www.sixt.com/fileadmin2/files/global/sideview/user_upload/fleet/png/1050x600";

export const CATEGORIES = [
  {
    slug: "economy",
    name: "Economy",
    startingFrom: 24,
    tagline: "Small, efficient, easy to park.",
    description:
      "The most affordable class we compare. Small sedans and hatchbacks such as the Kia Rio, Mitsubishi Mirage, and Chevrolet Spark from Alamo, Hertz, Enterprise and Sixt.",
    useCases: ["Solo travel", "Airport hops", "City stays"],
    passengers: "5",
    luggage: "2 bags",
    example: "Kia Rio, Mitsubishi Mirage, Chevrolet Spark",
    image: `${SIXT}/kia-rio-4d-weiss-2018.png`,
    popularCities: ["new-york", "chicago", "atlanta", "orlando"],
  },
  {
    slug: "compact",
    name: "Compact",
    startingFrom: 29,
    tagline: "A step up in comfort. Still nimble.",
    description:
      "More cabin room and trunk space than economy while keeping city-friendly dimensions. Nissan Sentra, Volkswagen Jetta, Toyota Corolla and similar.",
    useCases: ["Couples", "Weekend trips", "Downtown driving"],
    passengers: "5",
    luggage: "2 bags",
    example: "Nissan Sentra, Volkswagen Jetta, Toyota Corolla",
    image: `${SIXT}/nissan-sentra-4d-orange-2020.png`,
    popularCities: ["san-francisco", "los-angeles", "miami"],
  },
  {
    slug: "sedan",
    name: "Standard Sedan",
    startingFrom: 39,
    tagline: "The default road-trip class.",
    description:
      "Full-size and mid-size sedans deliver a smoother ride, larger trunk, and quieter cabin. Toyota Camry, Nissan Altima, Chevrolet Malibu — with BMW 3 Series available as a Standard Elite upgrade.",
    useCases: ["Business travel", "Family visits", "Interstate drives"],
    passengers: "5",
    luggage: "3 bags",
    example: "Toyota Camry, Nissan Altima, BMW 3 Series",
    image: `${SIXT}/bmw-3-sedan-4d-blue-2023-US.png`,
    popularCities: ["dallas", "houston", "chicago"],
  },
  {
    slug: "suv",
    name: "SUV",
    startingFrom: 55,
    tagline: "Room for luggage, gear, and mixed terrain.",
    description:
      "From mid-size crossovers to full-size SUVs — GMC Acadia, Ford Escape, Jeep Grand Cherokee, Chevrolet Tahoe. Ideal for families, ski trips, and larger groups.",
    useCases: ["Family trips", "Ski / mountain drives", "Group luggage"],
    passengers: "5-7",
    luggage: "4 bags",
    example: "GMC Acadia, Ford Escape, Chevrolet Tahoe",
    image: `${SIXT}/gmc-acadia-suv-silver-2026.png`,
    popularCities: ["las-vegas", "orlando", "atlanta"],
  },
  {
    slug: "luxury",
    name: "Luxury",
    startingFrom: 119,
    tagline: "Premium sedans for business & occasion travel.",
    description:
      "Executive luxury sedans — BMW 7 Series, Mercedes-Benz E-Class, Cadillac. Adaptive cruise, leather, and quiet cabins engineered for the long haul.",
    useCases: ["Executive travel", "Weddings", "Client meetings"],
    passengers: "5",
    luggage: "4 bags",
    example: "BMW 7 Series, Mercedes-Benz E-Class, Cadillac CT5",
    image: `${SIXT}/bmw-7-4d-blue-2023.png`,
    popularCities: ["new-york", "miami", "los-angeles"],
  },
  {
    slug: "convertible",
    name: "Convertible",
    startingFrom: 89,
    tagline: "Top down. Sunset roads.",
    description:
      "Two-seater roadsters and grand-tourer convertibles — BMW Z4 Convertible, Ford Mustang Convertible. Perfect for coastal drives in Miami, LA, or the Vegas desert.",
    useCases: ["Coastal drives", "Weekend escapes", "Special occasions"],
    passengers: "2-4",
    luggage: "2 bags",
    example: "BMW Z4, Ford Mustang Convertible",
    image: `${SIXT}/bmw-z4-convertible-white-2023-open.png`,
    popularCities: ["miami", "los-angeles", "las-vegas"],
  },
  {
    slug: "van",
    name: "Minivan",
    startingFrom: 79,
    tagline: "Seven+ seats and real luggage space.",
    description:
      "Chrysler Pacifica, Dodge Grand Caravan, Honda Odyssey, Toyota Sienna. Room for the family plus every suitcase.",
    useCases: ["Group trips", "Family reunions", "Airport shuttles"],
    passengers: "7-8",
    luggage: "5-6 bags",
    example: "Chrysler Pacifica, Honda Odyssey, Toyota Sienna",
    image: `${SIXT}/chrysler-pacifica-van-4d-black-2020-JV.png`,
    popularCities: ["orlando", "dallas", "atlanta"],
  },
  {
    slug: "pickup",
    name: "Pickup Truck",
    startingFrom: 69,
    tagline: "Work-ready, weekend-capable.",
    description:
      "Mid-size and full-size pickups from major providers — Toyota Tacoma, Ford F-150, Chevrolet Silverado, RAM 1500.",
    useCases: ["Moving day", "Job sites", "Trail heads"],
    passengers: "4-5",
    luggage: "Truck bed",
    example: "Toyota Tacoma, Ford F-150, Chevrolet Silverado",
    image: `${SIXT}/toyota-tacoma-dcab-grau-2017.png`,
    popularCities: ["houston", "dallas", "atlanta"],
  },
];

export const CITIES = [
  {
    slug: "new-york",
    name: "New York",
    startingFrom: 34,
    tagline: "JFK, LGA, Newark and Manhattan branches.",
    description:
      "Compare rentals across JFK, LaGuardia, Newark, and Manhattan pickup locations. Every major US brand serves the tri-state area.",
    image:
      "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?auto=format&fit=crop&w=1800&q=85",
    airports: ["JFK", "LGA", "EWR"],
    categories: ["economy", "compact", "sedan", "suv", "luxury"],
  },
  {
    slug: "los-angeles",
    name: "Los Angeles",
    startingFrom: 32,
    tagline: "LAX, Burbank, Long Beach — city-wide coverage.",
    description:
      "LAX is one of the busiest rental hubs in the US. Compare economy, luxury, and convertible options across every LA-area pickup point.",
    image:
      "https://images.unsplash.com/photo-1580655653885-65763b2597d0?auto=format&fit=crop&w=1800&q=85",
    airports: ["LAX", "BUR", "LGB"],
    categories: ["compact", "sedan", "convertible", "suv", "luxury"],
  },
  {
    slug: "miami",
    name: "Miami",
    startingFrom: 36,
    tagline: "MIA & FLL — convertibles a specialty.",
    description:
      "Compare rentals across MIA and Fort Lauderdale — including convertibles, luxury sedans, and family-friendly SUVs.",
    image:
      "https://images.unsplash.com/photo-1530071291164-537d481750f4?auto=format&fit=crop&w=1800&q=85",
    airports: ["MIA", "FLL"],
    categories: ["convertible", "luxury", "sedan", "suv"],
  },
  {
    slug: "orlando",
    name: "Orlando",
    startingFrom: 27,
    tagline: "MCO — one of America's busiest rental hubs.",
    description:
      "Orlando's rental market is enormous. Compare minivan, SUV, and economy pricing across every provider serving MCO and SFB.",
    image:
      "https://images.unsplash.com/photo-1597466599360-3b9775841aec?auto=format&fit=crop&w=1800&q=85",
    airports: ["MCO", "SFB"],
    categories: ["economy", "van", "suv", "sedan"],
  },
  {
    slug: "las-vegas",
    name: "Las Vegas",
    startingFrom: 30,
    tagline: "LAS Rent-A-Car Center — all brands, one roof.",
    description:
      "Las Vegas consolidates every major rental provider into a single Rent-A-Car Center. Compare convertibles, luxury, and SUVs for desert drives.",
    image:
      "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?auto=format&fit=crop&w=1800&q=85",
    airports: ["LAS"],
    categories: ["convertible", "luxury", "suv", "sedan"],
  },
  {
    slug: "chicago",
    name: "Chicago",
    startingFrom: 31,
    tagline: "O'Hare, Midway, and downtown pickups.",
    description:
      "Compare O'Hare and Midway rental options plus dozens of downtown Chicago branches. Popular for sedans and SUVs.",
    image:
      "https://images.unsplash.com/photo-1477414348463-c0eb7f1359b6?auto=format&fit=crop&w=1800&q=85",
    airports: ["ORD", "MDW"],
    categories: ["sedan", "suv", "economy", "luxury"],
  },
  {
    slug: "san-francisco",
    name: "San Francisco",
    startingFrom: 35,
    tagline: "SFO, OAK, SJC — Bay Area wide.",
    description:
      "Compact-friendly rentals for the city's tight streets, plus highway-ready sedans for Pacific Coast drives.",
    image:
      "https://images.unsplash.com/photo-1501594907352-04cda38ebc29?auto=format&fit=crop&w=1800&q=85",
    airports: ["SFO", "OAK", "SJC"],
    categories: ["compact", "sedan", "luxury", "suv"],
  },
  {
    slug: "houston",
    name: "Houston",
    startingFrom: 28,
    tagline: "IAH & Hobby — pickup and SUV country.",
    description:
      "Bush Intercontinental and Hobby host every major provider. Popular for pickup trucks, SUVs, and full-size sedans.",
    image:
      "https://images.unsplash.com/photo-1531218150217-54595bc2b934?auto=format&fit=crop&w=1800&q=85",
    airports: ["IAH", "HOU"],
    categories: ["pickup", "suv", "sedan", "van"],
  },
  {
    slug: "dallas",
    name: "Dallas",
    startingFrom: 29,
    tagline: "DFW — one of the largest US rental hubs.",
    description:
      "Compare full-size sedans, SUVs, and pickups across DFW and Love Field. Every brand at every terminal.",
    image:
      "https://images.unsplash.com/photo-1545194445-dddb8f4487c6?auto=format&fit=crop&w=1800&q=85",
    airports: ["DFW", "DAL"],
    categories: ["sedan", "pickup", "suv", "van"],
  },
  {
    slug: "atlanta",
    name: "Atlanta",
    startingFrom: 26,
    tagline: "ATL's consolidated rental center.",
    description:
      "Atlanta Hartsfield-Jackson runs a single consolidated rental facility, making side-by-side comparison simple.",
    image:
      "https://images.unsplash.com/photo-1575917649705-5b59aaa12e6b?auto=format&fit=crop&w=1800&q=85",
    airports: ["ATL"],
    categories: ["economy", "sedan", "suv", "van"],
  },
];

export const DRIVER_AGES = ["21-24", "25-29", "30-64", "65+"];

export const FAQS_GENERAL = [
  {
    q: "Is Carztohire a rental company?",
    a: "No. Carztohire is a car rental comparison and inquiry service. We do not own or operate a fleet. We help you compare options across major US providers including Alamo, Hertz, Sixt, Enterprise, Budget, Avis, National, Dollar, and Thrifty.",
  },
  {
    q: "Does Carztohire own cars?",
    a: "No. Every vehicle you rent through us is supplied by a licensed rental provider. We handle the comparison and quote request; the provider handles the vehicle, contract, and insurance.",
  },
  {
    q: "Which brands do you compare?",
    a: "Alamo, Hertz, Sixt, Enterprise, Budget, Avis, National, Dollar, and Thrifty — plus regional operators in select US cities.",
  },
  {
    q: "How do I receive a quote?",
    a: "Submit the search widget on any page or the inquiry form. A member of our team will contact you within one business hour with a side-by-side comparison of provider options that match your dates, city, and category.",
  },
  {
    q: "Are prices fixed?",
    a: "No. The prices shown are estimated 'starting-from' amounts based on recent averages across providers. Live availability and final pricing are confirmed by our team when we contact you.",
  },
  {
    q: "Does availability vary by city and provider?",
    a: "Yes. Fleet composition, class availability, and pricing differ by city, provider, season, and lead-driver age. Comparing across providers is exactly why an aggregator saves time and money.",
  },
  {
    q: "Do you charge a booking fee?",
    a: "No. Carztohire is free to use. We are compensated by our provider partners after a successful rental is completed — never by you.",
  },
  {
    q: "What information do I need to submit an inquiry?",
    a: "Just your pickup city, dates, preferred category, and lead-driver age. Contact details help us follow up by phone or email with a quote.",
  },
];

export function findCategory(slug) {
  return CATEGORIES.find((c) => c.slug === slug);
}
export function findCity(slug) {
  return CITIES.find((c) => c.slug === slug);
}
